import { Injectable } from '@angular/core';
import { BodyOutputType, Toast, ToasterService } from 'angular2-toaster';

@Injectable()
export class HttpErrorHandlerService {

  private readonly statusMessages: { [status: number]: string } = {
    503: 'Quick pause — we\'ll be right back!',
    // 404: 'The requested resource was not found.',
    // 401: 'Unauthorized access. Please check your credentials.',
    // 403: 'Access denied. You do not have permission to access this resource.',
    // 400: 'Bad request. Please check your input data.',
    // 500: 'Internal server error. Please try again later.',
    // 502: 'Bad gateway. Please try again later.',
    // 504: 'Gateway timeout. Please try again later.',
    // 507: 'Insufficient storage. Please try again later.',
  };

  /** 503 = direct outage; 0 = xhr CORS/network failure when server/preflight is down. */
  private readonly outageSourceStatuses = new Set<number>([503, 0]);

  private readonly quotes503: string[] = [
    'Patience is a virtue — and yours is legendary! 🏆',
    'Even the best servers need a coffee break. ☕',
    'Good things come to those who wait... and refresh. 🔄',
    'Not all heroes wear capes. Some restart servers. 🦸',
    'Rome wasn\'t built in a day, but this page might load in a sec! 🏛️',
    'Keep calm and try again. You\'ve got this! 💪',
    'Tiny detour on the info highway. 🛣️',
    'Our hamsters are running extra fast today. 🐹',
    'Plot twist: the server needed a nap. 😴',
    'Every cloud has a silver lining — this one is loading. ☁️',
    'Success is the sum of small retries. 🔁',
    'Adventure is out there — after this refresh! 🗺️',
    'Stars can\'t shine without darkness... or server restarts. ✨',
    'The comeback is always stronger than the setback. 🚀',
    'Life is 10% what happens and 90% how you refresh the page. 📱',
  ];

  private lastHttpStatus: number | null = null;
  private lastHttpStatusAt = 0;
  private toastAlreadyShown = false;
  private readonly ttlMs = 3000;

  capture(status: number): void {
    const normalized = this.normalizeCapturedStatus(status);
    if (normalized == null) {
      return;
    }

    const now = Date.now();
    const withinTtl = this.lastHttpStatusAt > 0 && (now - this.lastHttpStatusAt) <= this.ttlMs;
    const sameMappedStatus = this.lastHttpStatus === normalized;

    if (!(withinTtl && sameMappedStatus)) {
      this.toastAlreadyShown = false;
    }

    this.lastHttpStatus = normalized;
    this.lastHttpStatusAt = now;
  }

  showStatusToastIfMapped(toaster: ToasterService): void {
    if (!this.isRecent() || this.lastHttpStatus == null || this.toastAlreadyShown) {
      return;
    }

    const mapped = this.statusMessages[this.lastHttpStatus];
    if (!mapped) {
      return;
    }

    this.toastAlreadyShown = true;

    if (this.lastHttpStatus === 503) {
      toaster.pop(this.build503InfoToast(mapped));
      return;
    }

    toaster.pop({ type: 'error', title: 'Error', body: mapped });
  }

  resolveToast(type?: string, title?: string, body?: any): { type?: string; title?: string; body?: any; useTrustedHtml?: boolean; skip?: boolean } {
    if (type !== 'error' || !this.isRecent() || this.lastHttpStatus == null) {
      return { type, title, body };
    }

    const mapped = this.statusMessages[this.lastHttpStatus];
    if (!mapped || !this.isGenericErrorMessage(body)) {
      return { type, title, body };
    }

    if (this.toastAlreadyShown) {
      this.clear();
      return { skip: true };
    }

    this.toastAlreadyShown = true;

    if (this.lastHttpStatus === 503) {
      return {
        type: 'info',
        title: 'Info',
        body: this.build503ToastBody(mapped, this.getRandomQuote503()),
        useTrustedHtml: true,
      };
    }

    return { type, title, body: mapped };
  }

  private normalizeCapturedStatus(status: number): number | null {
    if (status == null || isNaN(status)) {
      return null;
    }
    if (this.outageSourceStatuses.has(status)) {
      return 503;
    }
    return this.statusMessages[status] ? status : null;
  }

  private build503InfoToast(mainMessage: string): Toast {
    return {
      type: 'info',
      title: 'Info',
      body: this.build503ToastBody(mainMessage, this.getRandomQuote503()),
      bodyOutputType: BodyOutputType.TrustedHtml,
    };
  }

  private getRandomQuote503(): string {
    return this.quotes503[Math.floor(Math.random() * this.quotes503.length)];
  }

  private build503ToastBody(mainMessage: string, quote: string): string {
    return `<div class="toast-503-body"><div class="toast-503-main">${mainMessage}</div><div class="toast-503-quote">${quote}</div></div>`;
  }

  private isRecent(): boolean {
    if (!this.lastHttpStatusAt) {
      return false;
    }
    if ((Date.now() - this.lastHttpStatusAt) > this.ttlMs) {
      this.clear();
      return false;
    }
    return true;
  }

  private isGenericErrorMessage(message: any): boolean {
    if (message == null || message === '') {
      return true;
    }
    return String(message).trim().toLowerCase().includes('something went wrong');
  }

  private clear(): void {
    this.lastHttpStatus = null;
    this.lastHttpStatusAt = 0;
    this.toastAlreadyShown = false;
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src322634822/src/src/app/shared/services/http-error-handler.service.ts